/**
 * Loads a script.
 * @param {string} src - Url of script to load.
 * @returns {Promise}
 */
function loadUcgScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

/**
 * Loads a stylesheet.
 * @param {string} href - Url of stylesheet to load.
 * @returns {Promise}
 */
function loadUcgStylesheet(href) {
  return new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.onload = () => resolve(link);
    link.onerror = () => reject;
    document.head.appendChild(link);
  });
}

/* global Modal, Swiper */

customElements.whenDefined('modal-dialog').then(() => {
  if (!customElements.get('ucg-videos-modal')) {
    class UcgVideosModal extends Modal {
      connectedCallback() {
        this.isCarousel = this.dataset.modalStyle === 'carousel';
        this.addEventListener('click', this.handleControlsClick.bind(this));
        this.bindVideoEvents();
      }

      /**
       * Closes on close button or overlay (outside the video).
       * @param {MouseEvent} evt - Click event.
       */
      handleClick(evt) {
        if (evt.target.closest('.js-close-modal')) {
          this.close();
          return;
        }

        if (evt.target.closest('.ucg-videos-modal__video-wrap')
          || evt.target.closest('[data-ucg-mute]')
          || evt.target.closest('[data-ucg-play]')
          || evt.target.closest('.slider-nav__btn')) {
          return;
        }

        if (evt.target === this
          || evt.target.classList.contains('modal__window')
          || evt.target.classList.contains('modal__content')
          || evt.target.classList.contains('ucg-videos-modal__container')
          || evt.target.classList.contains('ucg-videos-modal__stage')
          || evt.target.classList.contains('ucg-videos-modal__list')
          || evt.target.classList.contains('ucg-videos-modal__slide')
          || evt.target.classList.contains('ucg-videos-modal__item')
          || evt.target.classList.contains('swiper')
          || evt.target.classList.contains('swiper-wrapper')
          || evt.target.classList.contains('swiper-slide')) {
          this.close();
        }
      }

      bindVideoEvents() {
        this.querySelectorAll('video').forEach((video) => {
          video.addEventListener('play', () => this.updateControls(video));
          video.addEventListener('pause', () => this.updateControls(video));
        });
      }

      handleControlsClick(evt) {
        if (evt.target.closest('[data-ucg-mute]')) {
          evt.preventDefault();
          this.toggleMute();
          return;
        }

        if (evt.target.closest('[data-ucg-play]')) {
          evt.preventDefault();
          this.playActive();
          return;
        }

        if (evt.target.closest('.ucg-videos-modal__video-wrap')) {
          this.togglePlay();
        }
      }

      open(opener) {
        super.open(opener);
        const videoId = opener.dataset.videoId;

        if (this.isCarousel) {
          this.openCarousel(videoId);
        } else {
          this.openSingle(videoId);
        }
      }

      close() {
        this.pauseAllVideos();
        super.close();
      }

      openSingle(videoId) {
        const items = this.querySelectorAll('[data-video-id]');
        let activeItem = null;

        items.forEach((item) => {
          const isActive = item.dataset.videoId === videoId;
          item.hidden = !isActive;
          item.classList.toggle('is-active', isActive);
          if (isActive) activeItem = item;
        });

        this.hideSpinner();
        if (!activeItem) return;

        const video = activeItem.querySelector('video');
        this.pauseAllVideos(video);
        this.playVideo(video);
      }

      openCarousel(videoId) {
        if (!this.swiper) {
          if (theme.swiperLoaded) {
            this.initSwiper(videoId);
          } else {
            Promise.all([
              loadUcgScript(this.dataset.swiperJsPath),
              loadUcgStylesheet(this.dataset.swiperCssPath)
            ]).then(() => {
              theme.swiperLoaded = true;
              this.initSwiper(videoId);
            });
          }
        } else {
          const slide = this.querySelector(`.swiper-slide:not(.theme-clone)[data-video-id="${videoId}"]`);
          if (slide) {
            const videoIndex = parseInt(slide.dataset.swiperSlideIndex, 10);
            if (this.swiper.params.loop) {
              this.swiper.slideToLoop(videoIndex, 0, true);
            } else {
              this.swiper.slideTo(videoIndex, 0, true);
            }
          }
        }
      }

      initSwiper(videoId) {
        const slides = [...this.querySelectorAll('.swiper-slide')];
        let videoIndex = 0;
        slides.forEach((el, index) => {
          if (el.dataset.videoId === videoId) {
            videoIndex = index;
          }
          if (slides.length > 1) {
            el.parentElement.insertAdjacentHTML('beforeend', el.outerHTML);
            el.parentElement.lastElementChild.classList.add('theme-clone');
          }
        });

        this.swiper = new Swiper(`#${this.id} .swiper--ucg-videos`, {
          effect: 'coverflow',
          initialSlide: videoIndex,
          grabCursor: true,
          centeredSlides: true,
          slidesPerView: 1.35,
          spaceBetween: 16,
          breakpoints: {
            768: {
              slidesPerView: 3,
              spaceBetween: 0
            }
          },
          coverflowEffect: {
            rotate: 0,
            stretch: 0,
            depth: 400,
            modifier: 1,
            slideShadows: false
          },
          loop: slides.length > 1,
          navigation: {
            nextEl: `#${this.id} .swiper--ucg-videos--next`,
            prevEl: `#${this.id} .swiper--ucg-videos--prev`
          },
          a11y: {
            slideRole: null
          },
          on: {
            slideChange: (swiper) => {
              const video = swiper.slides[swiper.activeIndex]?.querySelector('video');
              if (!video) return;

              if (this.hasAttribute('open')) {
                this.pauseAllVideos(video);
                this.playVideo(video);
              } else {
                this.pauseAllVideos();
              }
            },
            afterInit: () => {
              setTimeout(() => {
                const video = this.swiper.slides[this.swiper.activeIndex]?.querySelector('video');
                if (video && this.hasAttribute('open')) {
                  this.playVideo(video);
                }
              }, 250);
            }
          }
        });
      }

      playVideo(video) {
        if (!video) return;
        video.muted = false;
        video.play().catch(() => {});
        this.updateControls(video);
      }

      playActive() {
        const video = this.getActiveVideo();
        if (!video) return;
        this.playVideo(video);
      }

      togglePlay() {
        const video = this.getActiveVideo();
        if (!video) return;

        if (!video.paused && !video.ended) {
          video.pause();
        } else {
          this.playVideo(video);
        }
        this.updateControls(video);
      }

      toggleMute() {
        const video = this.getActiveVideo();
        if (!video) return;

        const nextMuted = !video.muted;
        this.querySelectorAll('video').forEach((v) => {
          v.muted = nextMuted;
        });
        this.updateControls(video);
      }

      getActiveVideo() {
        if (this.isCarousel) {
          return this.swiper?.slides[this.swiper.activeIndex]?.querySelector('video');
        }
        return this.querySelector('[data-video-id].is-active video, [data-video-id]:not([hidden]) video');
      }

      getActiveSlide() {
        if (this.isCarousel) {
          return this.swiper?.slides[this.swiper.activeIndex];
        }
        return this.querySelector('[data-video-id].is-active, [data-video-id]:not([hidden])');
      }

      updateControls(videoParam) {
        const video = videoParam || this.getActiveVideo();
        const slide = video?.closest('[data-video-id]') || this.getActiveSlide();
        if (!video || !slide) return;

        const playing = !video.paused && !video.ended;
        const playBtn = slide.querySelector('[data-ucg-play]');
        const muteBtn = slide.querySelector('[data-ucg-mute]');

        playBtn?.toggleAttribute('hidden', playing);
        playBtn?.classList.toggle('is-paused', !playing);

        muteBtn?.querySelector('[data-ucg-mute-icon]')?.toggleAttribute('hidden', !video.muted);
        muteBtn?.querySelector('[data-ucg-unmute-icon]')?.toggleAttribute('hidden', video.muted);
      }

      pauseAllVideos(except) {
        this.querySelectorAll('video').forEach((video) => {
          if (video !== except) {
            video.pause();
            this.updateControls(video);
          }
        });
      }

      hideSpinner() {
        const spinner = this.querySelector('.loading-spinner');
        if (spinner) spinner.style.opacity = '0';
      }
    }

    customElements.define('ucg-videos-modal', UcgVideosModal);
  }
});
